import React, { useState } from "react";

interface Card {
  name: string;
  desc: string;
  tag: string;
  accent: "blue" | "teal" | "purple" | "green" | "amber";
  icon: React.ReactNode;
  // Página (ver App.js/OperativeApp `page` state) a la que navega la
  // tarjeta al hacer click — si no está presente, la tarjeta es solo
  // informativa (todavía no tiene una vista dedicada).
  path?: string;
  // Tiene funcionalidad real (no solo la tarjeta). En PRD solo se muestran
  // las disponibles; en QAS, todas (REACT_APP_MOSTRAR_MODULOS_EN_DESARROLLO,
  // mismo criterio que HomeLanding.tsx).
  disponible?: boolean;
}

const MOSTRAR_MODULOS_EN_DESARROLLO = process.env.REACT_APP_MOSTRAR_MODULOS_EN_DESARROLLO === 'true';

interface ToolChip {
  label: string;
  color: string;
}

const ACCENT_BORDER: Record<string, string> = {
  blue:   "linear-gradient(90deg, #0070f3, #00b4d8)",
  teal:   "linear-gradient(90deg, #00b4d8, #00d4aa)",
  purple: "linear-gradient(90deg, #7c3aed, #a855f7)",
  green:  "linear-gradient(90deg, #059669, #10b981)",
  amber:  "linear-gradient(90deg, #d97706, #f59e0b)",
};

const ICON_BG: Record<string, string> = {
  blue:   "rgba(0,112,243,0.18)",
  teal:   "rgba(0,180,216,0.18)",
  purple: "rgba(124,58,237,0.18)",
  green:  "rgba(5,150,105,0.18)",
  amber:  "rgba(217,119,6,0.18)",
};

const TAG_STYLE: Record<string, React.CSSProperties> = {
  blue:   { background: "rgba(0,112,243,0.15)",  color: "#4da6ff" },
  teal:   { background: "rgba(0,180,216,0.15)",  color: "#38d9f5" },
  purple: { background: "rgba(124,58,237,0.15)", color: "#c084fc" },
  green:  { background: "rgba(5,150,105,0.15)",  color: "#34d399" },
  amber:  { background: "rgba(217,119,6,0.15)",  color: "#fbbf24" },
};

const CARDS: Card[] = [
  {
    name: "Integration Suite",
    desc: "Integración de sistemas cloud y on-premise. iFlows, API Management y Event Mesh en un solo lugar.",
    tag: "iPaaS",
    accent: "blue",
    path: "btp-integration-suite",
    disponible: true,
    icon: (
      <svg viewBox="0 0 22 22" fill="none" width={22} height={22}>
        <circle cx="5"  cy="11" r="3" stroke="#4da6ff" strokeWidth="1.5"/>
        <circle cx="17" cy="5"  r="3" stroke="#4da6ff" strokeWidth="1.5"/>
        <circle cx="17" cy="17" r="3" stroke="#4da6ff" strokeWidth="1.5"/>
        <line x1="8" y1="10" x2="14" y2="6.5"  stroke="#4da6ff" strokeWidth="1.2" strokeLinecap="round"/>
        <line x1="8" y1="12" x2="14" y2="15.5" stroke="#4da6ff" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    name: "Cloud Identity Service",
    desc: "Gestión de identidades, autenticación SSO, roles y accesos para todo el ecosistema SAP.",
    tag: "IAS / IPS",
    accent: "teal",
    path: "btp-cloud-identity",
    icon: (
      <svg viewBox="0 0 22 22" fill="none" width={22} height={22}>
        <circle cx="11" cy="8" r="3.5" stroke="#38d9f5" strokeWidth="1.5"/>
        <path d="M4 19c0-3.866 3.134-7 7-7h0c3.866 0 7 3.134 7 7" stroke="#38d9f5" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M15 10l1.5 1.5L19 9" stroke="#38d9f5" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    name: "Master Data Integration",
    desc: "Sincronización y gobierno de datos maestros entre soluciones SAP y sistemas externos.",
    tag: "MDI / MDO",
    accent: "purple",
    icon: (
      <svg viewBox="0 0 22 22" fill="none" width={22} height={22}>
        <rect x="3"  y="5"  width="16" height="3" rx="1.5" stroke="#c084fc" strokeWidth="1.4"/>
        <rect x="3"  y="10" width="16" height="3" rx="1.5" stroke="#c084fc" strokeWidth="1.4"/>
        <rect x="3"  y="15" width="16" height="3" rx="1.5" stroke="#c084fc" strokeWidth="1.4"/>
        <circle cx="6.5" cy="6.5"  r="1" fill="#c084fc"/>
        <circle cx="6.5" cy="11.5" r="1" fill="#c084fc"/>
        <circle cx="6.5" cy="16.5" r="1" fill="#c084fc"/>
      </svg>
    ),
  },
  {
    name: "SAP Build",
    desc: "Plataforma low-code para apps, automatizaciones y sites empresariales sin necesidad de código avanzado.",
    tag: "Low-Code",
    accent: "green",
    icon: (
      <svg viewBox="0 0 22 22" fill="none" width={22} height={22}>
        <rect x="3"   y="13" width="7" height="7" rx="2" stroke="#34d399" strokeWidth="1.4"/>
        <rect x="12"  y="13" width="7" height="7" rx="2" stroke="#34d399" strokeWidth="1.4"/>
        <rect x="7.5" y="3"  width="7" height="7" rx="2" stroke="#34d399" strokeWidth="1.4"/>
        <line x1="11" y1="10" x2="6.5"  y2="13" stroke="#34d399" strokeWidth="1.2" strokeLinecap="round"/>
        <line x1="11" y1="10" x2="15.5" y2="13" stroke="#34d399" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    name: "HANA Cloud",
    desc: "Base de datos en memoria cloud-native con capacidades analíticas, graph y espaciales avanzadas.",
    tag: "DBaaS",
    accent: "amber",
    icon: (
      <svg viewBox="0 0 22 22" fill="none" width={22} height={22}>
        <ellipse cx="11" cy="8" rx="8" ry="4" stroke="#fbbf24" strokeWidth="1.4"/>
        <path d="M3 8v3c0 2.21 3.582 4 8 4s8-1.79 8-4V8"   stroke="#fbbf24" strokeWidth="1.4"/>
        <path d="M3 11v3c0 2.21 3.582 4 8 4s8-1.79 8-4v-3" stroke="#fbbf24" strokeWidth="1.4"/>
      </svg>
    ),
  },
  {
    name: "Analytics Cloud",
    desc: "BI, planificación empresarial y predicción aumentada con IA conectada al core SAP.",
    tag: "BI / AI",
    accent: "blue",
    icon: (
      <svg viewBox="0 0 22 22" fill="none" width={22} height={22}>
        <polyline points="3,16 8,10 12,13 17,6" stroke="#4da6ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <circle cx="17" cy="6" r="2" fill="#4da6ff"/>
        <line x1="3" y1="18" x2="19" y2="18" stroke="#4da6ff" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
  },
];

// Herramientas transversales, agrupadas bajo las pestañas "Gestión de BTP"
// (operación general de la plataforma) y "Administración de Subcuentas"
// (todo lo específico a una subaccount) — antes vivían juntas en un único
// "BTP Toolbox".
const GESTION_BTP: ToolChip[] = [
  { label: "BTP Cockpit",        color: "#4da6ff" },
  { label: "Alert Notification", color: "#c084fc" },
  { label: "SAP AI Core",        color: "#fbbf24" },
  { label: "Credential Store",   color: "#f87171" },
];

const SUBCUENTAS: ToolChip[] = [
  { label: "Subaccount Manager", color: "#38d9f5" },
  { label: "Cloud Connector",    color: "#34d399" },
];

type BtpSection = "servicios" | "gestion" | "subcuentas";

const TABS: { id: BtpSection; label: string }[] = [
  { id: "servicios",  label: "Servicios" },
  { id: "gestion",    label: "Gestión de BTP" },
  { id: "subcuentas", label: "Administración de Subcuentas" },
];

// Colores hardcodeados a la variable de tema equivalente (2026-08-20 — el
// módulo se armó con una paleta oscura fija, sin conectar al sistema de
// temas de src/index.css, así que el toggle claro/oscuro no le hacía nada.
// Los accent colors por categoría (blue/teal/purple/green/amber, arriba)
// quedan sin tocar a propósito, mismo criterio que el resto de la app
// ("blue-* queda sin remapear", ver tailwind.config.js) — son colores de
// marca por sección, no texto/fondo neutro.
const s = {
  root: {
    backgroundColor: "rgb(var(--c-bg))",
    minHeight: "100vh",
    padding: "48px 32px 64px",
    color: "rgb(var(--c-white))",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  } as React.CSSProperties,

  header: {
    textAlign: "center" as const,
    marginBottom: 52,
  },

  logoWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 64,
    height: 64,
    borderRadius: 18,
    background: "linear-gradient(135deg, #0070f3 0%, #00b4d8 100%)",
    marginBottom: 20,
  },

  title: {
    fontSize: 38,
    fontWeight: 700,
    letterSpacing: -1,
    color: "rgb(var(--c-white))",
    marginBottom: 10,
  },

  titleSpan: {
    background: "linear-gradient(90deg, #4da6ff, #00d4aa)",
    WebkitBackgroundClip: "text" as const,
    WebkitTextFillColor: "transparent",
    backgroundClip: "text" as const,
  },

  subtitle: {
    fontSize: 15,
    color: "rgb(var(--c-gray-400))",
    fontStyle: "italic" as const,
    maxWidth: 540,
    margin: "0 auto",
    lineHeight: 1.6,
  },

  tabBar: {
    display: "flex",
    gap: 4,
    marginBottom: 40,
    borderBottom: "1px solid rgb(var(--c-white) / 0.08)",
  },

  tabButton: (active: boolean): React.CSSProperties => ({
    padding: "12px 20px",
    fontSize: 13,
    fontWeight: 600,
    color: active ? "#4da6ff" : "rgb(var(--c-gray-400))",
    background: "transparent",
    border: "none",
    borderBottom: active ? "2px solid #4da6ff" : "2px solid transparent",
    cursor: "pointer",
    transition: "color 0.2s, border-color 0.2s",
  }),

  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase" as const,
    color: "#4da6ff",
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  sectionLine: {
    flex: 1,
    height: 1,
    background: "rgba(77,166,255,0.2)",
  },

  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
    marginBottom: 14,
  },

  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 16,
    marginBottom: 40,
  },

  card: {
    backgroundColor: "rgb(var(--c-surface))",
    border: "1px solid rgb(var(--c-white) / 0.07)",
    borderRadius: 16,
    padding: "28px 24px",
    cursor: "pointer",
    position: "relative" as const,
    overflow: "hidden" as const,
    transition: "border-color 0.2s, background 0.2s, transform 0.15s",
  },

  cardIconWrap: (accent: string): React.CSSProperties => ({
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 18,
    backgroundColor: ICON_BG[accent],
  }),

  cardName: {
    fontSize: 16,
    fontWeight: 600,
    color: "rgb(var(--c-white))",
    marginBottom: 8,
    letterSpacing: -0.2,
  },

  cardDesc: {
    fontSize: 13,
    color: "rgb(var(--c-gray-400))",
    lineHeight: 1.6,
  },

  cardTag: (accent: string): React.CSSProperties => ({
    display: "inline-block",
    marginTop: 16,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "3px 10px",
    borderRadius: 20,
    ...TAG_STYLE[accent],
  }),

  addCard: {
    background: "rgba(77,166,255,0.04)",
    border: "1px dashed rgba(77,166,255,0.25)",
    borderRadius: 16,
    padding: "28px 24px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    cursor: "pointer",
    minHeight: 160,
    transition: "background 0.2s, border-color 0.2s",
  },

  addIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(77,166,255,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    color: "#4da6ff",
    lineHeight: 1,
  },

  addLabel: { fontSize: 13, color: "#4da6ff", fontWeight: 500 },
  addSub:   { fontSize: 11, color: "rgb(var(--c-gray-500))", textAlign: "center" as const, lineHeight: 1.5 },

  toolbox: {
    backgroundColor: "rgb(var(--c-surface))",
    border: "1px solid rgb(var(--c-white) / 0.06)",
    borderRadius: 14,
    padding: "20px 24px",
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
  },

  toolboxTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "rgb(var(--c-gray-400))",
    marginBottom: 16,
    letterSpacing: "0.04em",
  },

  toolboxItems: { display: "flex", gap: 10, flexWrap: "wrap" as const },

  chip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "rgb(var(--c-white) / 0.04)",
    border: "1px solid rgb(var(--c-white) / 0.08)",
    borderRadius: 8,
    padding: "7px 13px",
    fontSize: 12,
    color: "rgb(var(--c-gray-400))",
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s, color 0.15s",
  },
};

function BtpCard({ card, onNavigate }: { card: Card; onNavigate?: (path: string) => void }) {
  const clickable = !!card.path;
  return (
    <div
      style={{ ...s.card, cursor: clickable ? "pointer" : "default" }}
      className={clickable ? "btp-card-clickable" : undefined}
      onClick={clickable ? () => onNavigate?.(card.path as string) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {/* top accent line */}
      <div
        style={{
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: 2,
          borderRadius: "16px 16px 0 0",
          background: ACCENT_BORDER[card.accent],
          opacity: 0.7,
        }}
      />
      <div style={s.cardIconWrap(card.accent)}>{card.icon}</div>
      <div style={s.cardName}>{card.name}</div>
      <div style={s.cardDesc}>{card.desc}</div>
      <div style={s.cardTag(card.accent)}>{card.tag}</div>
    </div>
  );
}

interface BtpPortalProps {
  requireAuth: (callback: () => void) => void;
  setPage: (page: string) => void;
}

export default function BtpPortal({ requireAuth, setPage }: BtpPortalProps) {
  const [section, setSection] = useState<BtpSection>("servicios");
  const visibles = CARDS.filter((c) => c.disponible || MOSTRAR_MODULOS_EN_DESARROLLO);
  const filas = [visibles.slice(0, 3), visibles.slice(3, 6)].filter((f) => f.length);
  // Las pestañas de gestión y subcuentas son solo visuales todavía: en PRD no se muestran.
  const tabs = MOSTRAR_MODULOS_EN_DESARROLLO ? TABS : TABS.filter((t) => t.id === "servicios");
  const handleNavigate = (path: string) => requireAuth(() => setPage(path));

  return (
    <div style={s.root}>
      {/* Los estilos de las tarjetas son objetos inline (sin :hover propio) —
          este bloque acota el efecto hover solo a las tarjetas navegables. */}
      <style>{`
        .btp-card-clickable:hover {
          border-color: rgba(77,166,255,0.4) !important;
          background-color: rgb(var(--c-white) / 0.04) !important;
          transform: translateY(-4px);
          box-shadow: 0 12px 30px rgba(0,112,243,0.18);
        }
      `}</style>
      {/* Header */}
      <div style={s.header}>
        <div style={s.logoWrap}>
          <svg viewBox="0 0 36 36" fill="none" width={36} height={36}>
            <rect x="4"  y="4"  width="12" height="12" rx="3" fill="white" opacity="0.95"/>
            <rect x="20" y="4"  width="12" height="12" rx="3" fill="white" opacity="0.6"/>
            <rect x="4"  y="20" width="12" height="12" rx="3" fill="white" opacity="0.6"/>
            <rect x="20" y="20" width="12" height="12" rx="3" fill="white" opacity="0.3"/>
          </svg>
        </div>
        <div style={s.title}>
          SAP <span style={s.titleSpan}>BTP</span>
        </div>
        <div style={s.subtitle}>
          "Plataforma tecnológica unificada para integración, identidad, datos y extensibilidad empresarial"
        </div>
      </div>

      {/* Tabs */}
      {tabs.length > 1 && (
      <div style={s.tabBar}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            style={s.tabButton(section === tab.id)}
            onClick={() => setSection(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      )}

      {/* --- SERVICIOS --- */}
      {section === "servicios" && (
        <>
          <div style={s.sectionLabel}>
            Soluciones principales
            <span style={s.sectionLine} />
          </div>

          {filas.map((fila, i) => (
            <div key={i} style={s.grid3}>
              {fila.map((card) => <BtpCard key={card.name} card={card} onNavigate={handleNavigate} />)}
            </div>
          ))}

          <div style={s.grid2}>
            <div style={s.addCard}>
              <div style={s.addIcon}>+</div>
              <div style={s.addLabel}>Agregar solución BTP</div>
              <div style={s.addSub}>
                Extensible conforme crecen<br />las necesidades del ecosistema
              </div>
            </div>
          </div>
        </>
      )}

      {/* --- GESTIÓN DE BTP --- */}
      {section === "gestion" && (
        <>
          <div style={s.sectionLabel}>
            Gestión de la plataforma
            <span style={s.sectionLine} />
          </div>

          <div style={s.grid2}>
            <div style={s.toolbox}>
              <div style={s.toolboxTitle}>Herramientas de gestión de BTP</div>
              <div style={s.toolboxItems}>
                {GESTION_BTP.map((chip) => (
                  <div key={chip.label} style={s.chip}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: chip.color, flexShrink: 0, display: "inline-block" }} />
                    {chip.label}
                  </div>
                ))}
              </div>
            </div>

            <div style={s.addCard}>
              <div style={s.addIcon}>+</div>
              <div style={s.addLabel}>Agregar herramienta de gestión</div>
              <div style={s.addSub}>
                Extensible conforme crecen<br />las necesidades de operación
              </div>
            </div>
          </div>
        </>
      )}

      {/* --- ADMINISTRACIÓN DE SUBCUENTAS --- */}
      {section === "subcuentas" && (
        <>
          <div style={s.sectionLabel}>
            Administración de subcuentas
            <span style={s.sectionLine} />
          </div>

          <div style={s.grid2}>
            <div style={s.toolbox}>
              <div style={s.toolboxTitle}>Herramientas de subcuentas</div>
              <div style={s.toolboxItems}>
                {SUBCUENTAS.map((chip) => (
                  <div key={chip.label} style={s.chip}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: chip.color, flexShrink: 0, display: "inline-block" }} />
                    {chip.label}
                  </div>
                ))}
              </div>
            </div>

            <div style={s.addCard}>
              <div style={s.addIcon}>+</div>
              <div style={s.addLabel}>Agregar herramienta de subcuenta</div>
              <div style={s.addSub}>
                Extensible conforme crecen<br />las necesidades por subaccount
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
